import { addDays, addMonths, addYears } from "date-fns";
import {
  EmployeeExitType,
  ExerciseWindowUnit,
  ExitForfeitureRule,
  ForfeitureSettings,
} from "../types/EsopPlan";

// ─── Map separation reason strings to EmployeeExitType ───────────────────────

const REASON_TO_EXIT_TYPE: Record<string, EmployeeExitType> = {
  RESIGNATION: EmployeeExitType.RESIGNATION,
  TERMINATION: EmployeeExitType.TERMINATION,
  RETIREMENT: EmployeeExitType.RETIREMENT,
  PERFORMANCE_RELATED: EmployeeExitType.PERFORMANCE_RELATED,
  MISCONDUCT: EmployeeExitType.MISCONDUCT,
  PERMANENT_DISABILITY: EmployeeExitType.PERMANENT_DISABILITY,
  DEATH: EmployeeExitType.DEATH,
  ABANDONMENT: EmployeeExitType.ABANDONMENT,
};

export function mapReasonToExitType(
  reason?: string
): EmployeeExitType | undefined {
  if (!reason) return undefined;
  return REASON_TO_EXIT_TYPE[reason.toUpperCase()];
}

// ─── Look up the exit rule for a given exit type ─────────────────────────────

export function getExitRule(
  settings: ForfeitureSettings,
  exitType: EmployeeExitType
): ExitForfeitureRule | undefined {
  return settings.exitRules.find((r) => r.exitType === exitType);
}

// ─── Compute last date to exercise ──────────────────────────────────────────

/**
 * Frontend mirror of the backend `getLastDateToExerciseFromPlan`.
 * Returns the date until which the employee can exercise their vested options,
 * or `undefined` if the window never expires or no applicable rule exists.
 */
export function computeLastDateToExercise(
  lastDay: Date,
  settings: ForfeitureSettings,
  reason?: string
): Date | undefined {
  const exitType = mapReasonToExitType(reason);
  if (!exitType) return undefined;

  const rule = getExitRule(settings, exitType);
  if (!rule) return undefined;

  const { exerciseWindow } = rule.vestedOptions;
  if (exerciseWindow.neverExpires) return undefined;

  const { value, unit } = exerciseWindow;
  switch (unit) {
    case ExerciseWindowUnit.DAYS:
      return addDays(lastDay, value);
    case ExerciseWindowUnit.MONTHS:
      return addMonths(lastDay, value);
    case ExerciseWindowUnit.YEARS:
      return addYears(lastDay, value);
    default:
      return addDays(lastDay, value);
  }
}

// ─── Format exercise window for display ─────────────────────────────────────

export function formatExerciseWindow(window: {
  value: number;
  unit: ExerciseWindowUnit;
  neverExpires?: boolean;
}): string {
  if (window.neverExpires) return "Never expires";
  const unitLabel =
    window.value === 1
      ? window.unit.charAt(0) +
        window.unit.slice(1).toLowerCase().replace(/s$/, "")
      : window.unit.charAt(0) + window.unit.slice(1).toLowerCase();
  return `${window.value} ${unitLabel}`;
}

// ─── Human-readable label for expiry mode ──────────────────────────────────

export function formatExpiryMode(
  activeEmployee: ForfeitureSettings["activeEmployee"]
): string {
  const val = activeEmployee.expiryValue ?? "?";
  const unit = (activeEmployee.expiryUnit ?? "MONTHS").toLowerCase();
  switch (activeEmployee.expiryMode) {
    case "GRANT_EXPIRY":
      return `Expires ${val} ${unit} from grant date`;
    case "FROM_VESTING_DATE":
      return `Expires ${val} ${unit} from vesting date`;
    case "NEVER":
      return "Never expires";
    default:
      return activeEmployee.expiryMode;
  }
}
